import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Autocomplete, Stack, TextField, Typography } from "@mui/material";

export interface YesNoAutocompleteProps {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  label?: ReactNode;
  labelAbove?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  yesLabel?: string;
  noLabel?: string;
  id?: string;
  blurOnSelect?: boolean;
  ariaLabelledBy?: string;
  disableClearable?: boolean;
}

export function YesNoAutocomplete({
  value,
  onChange,
  label,
  labelAbove,
  required,
  disabled,
  error,
  helperText,
  yesLabel,
  noLabel,
  id,
  blurOnSelect,
  ariaLabelledBy,
  disableClearable,
}: YesNoAutocompleteProps) {
  const { t } = useTranslation();

  const yes = yesLabel ?? t("common.yes");
  const no = noLabel ?? t("common.no");

  const toLabel = (val: boolean | null): string | null => (val === null ? null : val ? yes : no);
  const fromLabel = (optionLabel: string | null): boolean | null => (optionLabel === null ? null : optionLabel === yes);

  const labelId = labelAbove && id ? `${id}-label` : ariaLabelledBy;

  const autocomplete = (
    <Autocomplete<string, false, boolean>
      id={id}
      value={toLabel(value)}
      options={[yes, no]}
      disabled={disabled}
      blurOnSelect={blurOnSelect}
      disableClearable={disableClearable ?? false}
      isOptionEqualToValue={(option, val) => option === val}
      getOptionLabel={(option) => option}
      onChange={(_, newValue) => onChange(fromLabel(newValue))}
      fullWidth
      renderInput={(params) => (
        <TextField
          sx={{
            minWidth: "0em",
            "& .MuiInputBase-inputSizeSmall": { fontSize: "0.875rem" },
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
              paddingY: "2px",
            },
          }}
          {...params}
          margin="none"
          variant="outlined"
          size="small"
          slotProps={{
            inputLabel: { sx: { fontSize: "0.875rem" }, required },
            htmlInput: { ...params.inputProps, "aria-labelledby": labelId, "aria-required": required },
          }}
          error={error}
          helperText={helperText ?? " "}
          label={labelAbove ? undefined : label}
        />
      )}
    />
  );

  if (labelAbove) {
    return (
      <Stack spacing={0.5}>
        <Typography id={labelId} variant="caption" sx={{ fontWeight: 500 }}>
          {label}
          {required && <span aria-hidden> *</span>}
        </Typography>
        {autocomplete}
      </Stack>
    );
  }

  return autocomplete;
}
